/**
 * storeIdentity.ts — the store's own GSTIN and home state code.
 *
 * Lives outside `reports/` because it is not a reporting concern: billing needs
 * it to decide place of supply on an outward supply, and purchasing needs it for
 * an inward one. Reporting merely reads the same fact.
 */

import { database } from '@/db';
import Store from '@/db/models/Store';
import Merchant from '@/db/models/Merchant';
import { stateCodeFromGstin, stateCodeFromName } from '@/reports/gst/gstStates';

export interface StoreGstIdentity {
  storeName: string;
  gstin: string | null;
  homeStateCode: string | null;
}

/**
 * Resolve a store's own GSTIN and home state code. A store-level GSTIN override
 * wins over the merchant's; the state falls back from GSTIN → store address →
 * merchant address.
 */
export async function loadStoreGstIdentity(storeId: string): Promise<StoreGstIdentity> {
  const store = await database.get<Store>('stores').find(storeId);
  let merchant: Merchant | null = null;
  try {
    merchant = await database.get<Merchant>('merchants').find(store.merchantId);
  } catch {
    merchant = null;
  }

  const gstin = store.gstinOverride || merchant?.gstin || null;
  const homeStateCode =
    stateCodeFromGstin(gstin) || stateCodeFromName(store.state) || stateCodeFromName(merchant?.state ?? null);

  return { storeName: store.name, gstin, homeStateCode };
}
